import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { PAGINATION_CONSTANTS } from '../constants/pagination.constants';
import { AutomaticMeasuresService } from '../services/api/generated/automatic-measures/automatic-measures.service';
import type { GetAutomaticMeasureFollowUpListParams, AutomaticMeasureFollowUpPagination } from '../services/api/generated/model';
import { LazyLoadParams } from '../types/lazy-load.interface';
import { AutomaticMeasureFollow } from '../types/measure.interface';

const MAX_CACHED_ASSETS = 10;

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
  currentAssetId: number | null;
}

interface CachedAssetData {
  measures: AutomaticMeasureFollow[];
  pagination: PaginationState;
  lastAccessed: number;
}

interface AutomaticMeasureFollowState {
  assetCache: Record<number, CachedAssetData>;
  paginationState: PaginationState;
}

const initialPaginationState: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  limit: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
  currentAssetId: null,
};

const initialState: AutomaticMeasureFollowState = {
  assetCache: {},
  paginationState: initialPaginationState,
};

const toQueryParams = (
  assetId: number,
  page: number,
  pageSize: number,
  lazy?: LazyLoadParams,
): GetAutomaticMeasureFollowUpListParams => ({
  assetId,
  page,
  pageSize,
  ...(lazy?.sorts?.length ? { sorts: JSON.stringify(lazy.sorts) } : {}),
  ...(lazy?.filters?.length ? { filters: JSON.stringify(lazy.filters) } : {}),
});

const evictOldestIfFull = (cache: Record<number, CachedAssetData>): Record<number, CachedAssetData> => {
  const entries = Object.entries(cache);
  if (entries.length < MAX_CACHED_ASSETS) return cache;
  const [oldestId] = entries.sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed)[0] ?? [];
  if (!oldestId) return cache;
  const { [Number(oldestId)]: _, ...remaining } = cache;
  return remaining;
};

export const AutomaticMeasureFollowUpStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),
  withMethods(store => {
    const api = inject(AutomaticMeasuresService);

    const fetchPage = (assetId: number, page: number, pageSize: number, lazy?: LazyLoadParams) =>
      firstValueFrom(
        api.getAutomaticMeasureFollowUpList<AutomaticMeasureFollowUpPagination>(toQueryParams(assetId, page, pageSize, lazy)),
      );

    const writePage = (assetId: number, response: AutomaticMeasureFollowUpPagination): void => {
      const measures = response.measures as unknown as AutomaticMeasureFollow[];
      const pagination: PaginationState = {
        currentPage: response.page,
        totalPages: response.pages,
        totalItems: response.total,
        limit: response.limit,
        currentAssetId: assetId,
      };
      const cleanedCache = evictOldestIfFull(store.assetCache());
      patchState(store, {
        assetCache: {
          ...cleanedCache,
          [assetId]: { measures, pagination, lastAccessed: Date.now() },
        },
        paginationState: pagination,
      });
    };

    return {
      async loadPaginatedByAssetId(assetId: number, params: LazyLoadParams): Promise<void> {
        const response = await fetchPage(assetId, params.pagination.page, params.pagination.pageSize, params);
        writePage(assetId, response);
      },

      measuresByAssetId: (assetId: number): AutomaticMeasureFollow[] => store.assetCache()[assetId]?.measures ?? [],

      invalidateAsset(assetId: number): void {
        const { [assetId]: _, ...remaining } = store.assetCache();
        patchState(store, { assetCache: remaining });
      },
    };
  }),
  withComputed(store => ({
    hasMoreItems: computed(() => {
      const pagination = store.paginationState();
      return pagination.currentPage < pagination.totalPages;
    }),
  })),
);
